import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';
import Swal from 'sweetalert2';

const Employees = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    companyId: '',
    title: 'Herr',
    firstName: '',
    lastName: '',
    email: '',
    birthDate: '',
    birthPlace: '',
    maritalStatus: 'Ledig',
    nationality: '',
    address: '',
    postalCode: '',
    city: '',
    employmentStartDate: '',
    employmentEndDate: '',
    jobTitle: '',
    salaryType: 'monthly',
    salaryAmount: '',
    weeklyWorkingHours: ''
  });

  // Fetch employees
  const { data: employees = [], isLoading } = useQuery({
    queryKey: ['employees'],
    queryFn: async () => {
      const response = await api.get('/employees');
      return response.data;
    }
  });

  // Fetch companies
  const { data: companies = [] } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await api.get('/companies');
      return response.data;
    }
  });

  // Create/Update mutation
  const saveMutation = useMutation({
    mutationFn: async (data) => {
      if (editingEmployee) {
        const response = await api.put(`/employees/${editingEmployee._id}`, data);
        return response.data;
      } else {
        const response = await api.post('/employees', data);
        return response.data;
      }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries(['employees']);
      setShowForm(false);
      setEditingEmployee(null);
      resetForm();
      Swal.fire({
        icon: 'success',
        title: t('common.success'),
        text: editingEmployee ? t('employees.updatedSuccessfully') : t('employees.createdSuccessfully'),
        confirmButtonText: t('common.ok')
      });
    },
    onError: (error) => {
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: error.response?.data?.message || t('employees.saveError'),
        confirmButtonText: t('common.ok')
      });
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/employees/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['employees']);
      Swal.fire({
        icon: 'success',
        title: t('common.success'),
        text: t('employees.deletedSuccessfully'),
        confirmButtonText: t('common.ok')
      });
    },
    onError: (error) => {
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: error.response?.data?.message || t('employees.deleteError'),
        confirmButtonText: t('common.ok')
      });
    }
  });

  const resetForm = () => {
    setFormData({
      companyId: '',
      title: 'Herr',
      firstName: '',
      lastName: '',
      email: '',
      birthDate: '',
      birthPlace: '',
      maritalStatus: 'Ledig',
      nationality: '',
      address: '',
      postalCode: '',
      city: '',
      employmentStartDate: '',
      employmentEndDate: '',
      jobTitle: '',
      salaryType: 'monthly',
      salaryAmount: '',
      weeklyWorkingHours: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      companyId: employee.companyId._id,
      title: employee.title,
      firstName: employee.firstName,
      lastName: employee.lastName,
      email: employee.email || '',
      birthDate: employee.birthDate ? new Date(employee.birthDate).toISOString().split('T')[0] : '',
      birthPlace: employee.birthPlace,
      maritalStatus: employee.maritalStatus,
      nationality: employee.nationality,
      address: employee.address,
      postalCode: employee.postalCode,
      city: employee.city,
      employmentStartDate: employee.employmentStartDate ? new Date(employee.employmentStartDate).toISOString().split('T')[0] : '',
      employmentEndDate: employee.employmentEndDate ? new Date(employee.employmentEndDate).toISOString().split('T')[0] : '',
      jobTitle: employee.jobTitle,
      salaryType: employee.salaryType,
      salaryAmount: employee.salaryAmount,
      weeklyWorkingHours: employee.weeklyWorkingHours
    });
    setShowForm(true);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: t('employees.confirmDelete'),
      text: t('employees.cannotRevert'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('employees.yesDelete'),
      cancelButtonText: t('common.cancel')
    }).then((result) => {
      if (result.isConfirmed) {
        deleteMutation.mutate(id);
      }
    });
  };

  const handleSave = () => {
    if (!formData.companyId || !formData.firstName || !formData.lastName) {
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: t('employees.pleaseFillRequiredFields'),
        confirmButtonText: t('common.ok')
      });
      return;
    }
    
    // Convert string values to numbers for backend validation
    const dataToSend = {
      ...formData,
      salaryAmount: parseFloat(formData.salaryAmount),
      weeklyWorkingHours: parseInt(formData.weeklyWorkingHours)
    };
    
    saveMutation.mutate(dataToSend);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingEmployee(null);
    resetForm();
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">{t('employees.title')}</h1>
        <button
          onClick={() => setShowForm(true)}
          className="btn btn-primary"
        >
          {t('employees.addEmployee')}
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl max-h-full overflow-auto w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">
                {editingEmployee ? t('employees.editEmployee') : t('employees.addEmployee')}
              </h2>
              <button onClick={handleCancel} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Company Selection */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('employees.company')} *
                </label>
                <select
                  name="companyId"
                  value={formData.companyId}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">{t('employees.selectCompany')}</option>
                  {companies.map(company => (
                    <option key={company._id} value={company._id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('employees.title')} *
                </label>
                <select
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Herr">{t('employees.herr')}</option>
                  <option value="Frau">{t('employees.frau')}</option>
                </select>
              </div>

              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('employees.firstName')} *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('employees.lastName')} *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Email */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('employees.email')}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('employees.emailPlaceholder')}
                />
              </div>

              {/* Birth Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('employees.birthDate')} *
                </label>
                <input
                  type="date"
                  name="birthDate"
                  value={formData.birthDate}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Birth Place */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('employees.birthPlace')} *
                </label>
                <input
                  type="text"
                  name="birthPlace"
                  value={formData.birthPlace}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Marital Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('employees.maritalStatus')} *
                </label>
                <select
                  name="maritalStatus"
                  value={formData.maritalStatus}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Ledig">{t('employees.ledig')}</option>
                  <option value="Verheiratet">{t('employees.verheiratet')}</option>
                  <option value="Geschieden">{t('employees.geschieden')}</option>
                  <option value="Verwitwet">{t('employees.verwitwet')}</option>
                </select>
              </div>

              {/* Nationality */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('employees.nationality')} *
                </label>
                <input
                  type="text"
                  name="nationality"
                  value={formData.nationality}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Address */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('employees.address')} *
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Postal Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('employees.postalCode')} *
                </label>
                <input
                  type="text"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('employees.city')} *
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Employment Start Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('employees.employmentStartDate')} *
                </label>
                <input
                  type="date"
                  name="employmentStartDate"
                  value={formData.employmentStartDate}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Employment End Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('employees.employmentEndDate')}
                </label>
                <input
                  type="date"
                  name="employmentEndDate"
                  value={formData.employmentEndDate}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Job Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('employees.jobTitle')} *
                </label>
                <input
                  type="text"
                  name="jobTitle"
                  value={formData.jobTitle}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Salary Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('employees.salaryType')} *
                </label>
                <select
                  name="salaryType"
                  value={formData.salaryType}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="monthly">{t('employees.monthly')}</option>
                  <option value="hourly">{t('employees.hourly')}</option>
                </select>
              </div>

              {/* Salary Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('employees.salaryAmount')} *
                </label>
                <input
                  type="number"
                  name="salaryAmount"
                  value={formData.salaryAmount}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              {/* Weekly Working Hours */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('employees.weeklyWorkingHours')} *
                </label>
                <input
                  type="number"
                  name="weeklyWorkingHours"
                  value={formData.weeklyWorkingHours}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                  max="60"
                  required
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button onClick={handleCancel} className="btn btn-secondary">
                {t('common.cancel')}
              </button>
              <button onClick={handleSave} className="btn btn-primary" disabled={saveMutation.isPending}>
                {saveMutation.isPending ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Employees Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('employees.name')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('employees.company')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('employees.jobTitle')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('employees.salaryType')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('employees.salaryAmount')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('employees.weeklyWorkingHours')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('common.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {employees.map((employee) => (
              <tr key={employee._id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {employee.fullName}
                  </div>
                  <div className="text-sm text-gray-500">
                    {employee.birthDate ? new Date(employee.birthDate).toLocaleDateString() : ''}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {employee.companyId?.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {employee.jobTitle}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {t(`employees.${employee.salaryType}`)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {employee.salaryAmount} €
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {employee.weeklyWorkingHours} {t('employees.hours')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => handleEdit(employee)}
                    className="text-blue-600 hover:text-blue-900 mr-3"
                  >
                    {t('common.edit')}
                  </button>
                  <button
                    onClick={() => handleDelete(employee._id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    {t('common.delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Employees;
